<template>
  <div>
    <Header :role="role" :header="$t('LIMIT_REQUEST_DETAILS')" showSettings />
    <b-container class="container-wide">
      <div class="mt-5" />

      <b-alert show variant="info" v-if="loading" class="text-center">
        <b-spinner small class="mr-2"></b-spinner>
        {{ $t('LOADING') }}...
      </b-alert>

      <!-- Back button above card -->
      <div v-if="!loading && limitRequest" class="mb-3">
        <b-button
          variant="secondary"
          size="sm"
          @click="$router.go(-1)"
        >
          <i class="fa fa-arrow-left mr-1"></i>
          {{ $t('BACK') }}
        </b-button>
      </div>

      <b-card v-if="!loading && limitRequest">
        <b-card-header>
          <h4>
            {{ $t('LIMIT_REQUEST') }} #{{ limitRequest.id }}
            <b-badge
              :variant="statusVariant(limitRequest.lastResult)"
              class="ml-2"
            >
              {{ formatResult(limitRequest.lastResult) }}
            </b-badge>
          </h4>
        </b-card-header>

        <b-card-body>
          <!-- Company Information -->
          <b-row class="mb-4">
            <b-col md="12">
              <h5 class="border-bottom pb-2 mb-3">
                {{ $t('COMPANY_INFORMATION') }}
              </h5>
            </b-col>
            <b-col md="6">
              <dl class="row">
                <dt class="col-sm-5">{{ $t('COMPANY_NAME') }}:</dt>
                <dd class="col-sm-7">
                  <strong>{{ limitRequest.companyName || limitRequest.regCode || '-' }}</strong>
                </dd>

                <dt class="col-sm-5">{{ $t('REGISTRY_CODE') }}:</dt>
                <dd class="col-sm-7">{{ limitRequest.regCode || '-' }}</dd>
              </dl>
            </b-col>
            <b-col md="6">
              <dl class="row">
                <dt class="col-sm-5">{{ $t('CONTACT_EMAIL') }}:</dt>
                <dd class="col-sm-7">{{ limitRequest.contactEmail || '-' }}</dd>

                <dt class="col-sm-5">{{ $t('CONTACT_PHONE') }}:</dt>
                <dd class="col-sm-7">{{ limitRequest.contactPhone || '-' }}</dd>
              </dl>
            </b-col>
          </b-row>

          <!-- Representative Information -->
          <b-row class="mb-3">
            <b-col md="12">
              <h5 class="border-bottom pb-2 mb-2">
                {{ $t('REPRESENTATIVE_INFORMATION') }}
              </h5>
            </b-col>
            <b-col md="12">
              <dl class="row compact-dl">
                <dt class="col-sm-3">{{ $t('AUTHENTICATED_PERSON_NAME') }}:</dt>
                <dd class="col-sm-3">{{ limitRequest.authenticatedPersonName || '-' }}</dd>

                <dt class="col-sm-3">{{ $t('AUTHENTICATED_PERSON_CODE') }}:</dt>
                <dd class="col-sm-3">{{ limitRequest.authenticatedPersonCode || '-' }}</dd>

                <dt class="col-sm-3">{{ $t('AUTH_METHOD') }}:</dt>
                <dd class="col-sm-3">{{ limitRequest.authenticationMethod || '-' }}</dd>

                <dt class="col-sm-3">{{ $t('POWER_OF_ATTORNEY') }}:</dt>
                <dd class="col-sm-3">
                  <b-badge :variant="limitRequest.powerOfAttorney ? 'success' : 'secondary'">
                    {{ limitRequest.powerOfAttorney ? $t('YES') : $t('NO') }}
                  </b-badge>
                </dd>

                <dt class="col-sm-3">{{ $t('RATE_LIMIT_HOURLY') }}:</dt>
                <dd class="col-sm-3">
                  <span v-if="rateLimitInfo">
                    {{ rateLimitInfo.remainingRequests }} / {{ rateLimitInfo.maxRequestsPerHour }}
                  </span>
                  <span v-else>-</span>
                  <b-button
                    v-if="limitRequest.authenticatedPersonCode"
                    variant="link"
                    size="sm"
                    class="p-0 ml-2 text-danger"
                    style="font-size: 0.75rem; text-decoration: underline;"
                    @click="resetRateLimit"
                  >
                    Lähtesta
                  </b-button>
                </dd>
              </dl>
            </b-col>
          </b-row>

          <!-- Decision Information -->
          <b-row class="mb-4">
            <b-col md="12">
              <h5 class="border-bottom pb-2 mb-3">
                {{ $t('DECISION_INFORMATION') }}
                <b-badge v-if="limitRequest.isHigherLimitRequest" variant="info" class="ml-2">
                  {{ $t('HIGHER_LIMIT_REQUEST_BADGE') }}
                </b-badge>
              </h5>
            </b-col>
            <b-col md="6">
              <dl class="row">
                <dt class="col-sm-5">{{ $t('DECISION_STATUS') }}:</dt>
                <dd class="col-sm-7">
                  <b-badge :variant="statusVariant(limitRequest.lastResult)">
                    {{ formatResult(limitRequest.lastResult) }}
                  </b-badge>
                </dd>

                <dt class="col-sm-5">{{ $t('REQUESTED_LIMIT') }}:</dt>
                <dd class="col-sm-7">
                  <span v-if="limitRequest.requestedAmount && limitRequest.requestedAmount > 0">
                    <strong>{{ limitRequest.requestedAmount }} EUR</strong>
                  </span>
                  <span v-else class="text-muted">
                    {{ $t('INITIAL_LIMIT_CHECK') }}
                  </span>
                </dd>

                <dt class="col-sm-5">{{ $t('ISSUED_LIMIT') }}:</dt>
                <dd class="col-sm-7">
                  <strong v-if="limitRequest.issuedLimit > 0" class="text-success">
                    {{ limitRequest.issuedLimit }} EUR
                  </strong>
                  <span v-else>-</span>
                </dd>
              </dl>
            </b-col>
            <b-col md="6">
              <dl class="row">
                <dt class="col-sm-5">{{ $t('EXPIRES_AT') }}:</dt>
                <dd class="col-sm-7">
                  {{ limitRequest.expiresAt ? formatDateTime(limitRequest.expiresAt) : '-' }}
                </dd>

                <dt class="col-sm-5">{{ $t('CONSENT_GIVEN') }}:</dt>
                <dd class="col-sm-7">
                  <b-badge :variant="limitRequest.consentGiven ? 'success' : 'secondary'">
                    {{ limitRequest.consentGiven ? $t('YES') : $t('NO') }}
                  </b-badge>
                </dd>
              </dl>
            </b-col>
          </b-row>

          <!-- Timestamps -->
          <b-row class="mb-4">
            <b-col md="12">
              <h5 class="border-bottom pb-2 mb-3">
                {{ $t('TIMESTAMPS') }}
              </h5>
            </b-col>
            <b-col md="6">
              <dl class="row">
                <dt class="col-sm-5">{{ $t('CREATED_AT') }}:</dt>
                <dd class="col-sm-7">
                  {{ formatDateTime(limitRequest.createdAt) }}
                </dd>
              </dl>
            </b-col>
            <b-col md="6">
              <dl class="row">
                <dt class="col-sm-5">{{ $t('UPDATED_AT') }}:</dt>
                <dd class="col-sm-7">
                  {{ formatDateTime(limitRequest.updatedAt) }}
                </dd>
              </dl>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <b-alert show variant="danger" v-if="!loading && !limitRequest">
        {{ $t('LIMIT_REQUEST_NOT_FOUND') }}
      </b-alert>

      <RenderDeleteModal
        :show="showResetModal"
        @ok="confirmResetRateLimit"
        @cancel="closeResetModal"
        :label="$t('CONFIRM_RESET_RATE_LIMIT')"
      />
    </b-container>
  </div>
</template>

<script>
import { Header, RenderDeleteModal } from '@/components/common';
import axios from 'axios';

export default {
  name: 'LimitRequestDetail',
  components: {
    Header,
    RenderDeleteModal,
  },
  props: {
    role: String,
    limitId: [String, Number],
  },
  data() {
    return {
      loading: false,
      limitRequest: null,
      showResetModal: false,
      rateLimitInfo: null,
    };
  },
  mounted() {
    this.fetchLimitRequest();
  },
  methods: {
    async fetchLimitRequest() {
      this.loading = true;
      try {
        const response = await axios.get(`/limit-requests/${this.limitId}`);
        const data = response.data;

        // Map snake_case API response to camelCase
        this.limitRequest = {
          id: data.id,
          companyName: data.company_name,
          regCode: data.regno,
          partner: data.partner,
          partnerRegCode: data.partner_reg_code,
          contactEmail: data.contact_email,
          contactPhone: data.contact_phone,
          authenticatedPersonName: data.authenticated_person_name,
          authenticatedPersonCode: data.authenticated_person_code,
          authenticationMethod: data.authentication_method,
          powerOfAttorney: data.power_of_attorney,
          consentGiven: data.consent_given,
          limitSlug: data.limit_slug,
          source: data.source,
          requestedAmount: data.amount,
          isHigherLimitRequest: data.is_higher_limit_request,
          lastResult: data.result_status,
          issuedLimit: data.result_amount,
          expiresAt: data.result_expires_at,
          parentLimitId: data.parent_limit_id,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
        };

        // Fetch rate limit info if we have authenticated person code
        if (this.limitRequest.authenticatedPersonCode) {
          this.fetchRateLimitInfo();
        }
      } catch (error) {
        console.error('Error fetching limit request:', error);
        this.$bvToast.toast(this.$t('ERROR_LOADING_LIMIT_REQUEST'), {
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchRateLimitInfo() {
      try {
        const response = await axios.get(`/limit-requests/remaining/${this.limitRequest.authenticatedPersonCode}`);
        this.rateLimitInfo = response.data;
      } catch (error) {
        console.error('Error fetching rate limit info:', error);
        // Silently fail - just won't show rate limit info
      }
    },
    resetRateLimit() {
      this.showResetModal = true;
    },
    closeResetModal() {
      this.showResetModal = false;
    },
    async confirmResetRateLimit() {
      this.showResetModal = false;

      try {
        await axios.post(`/limit-requests/reset-rate-limit/${this.limitRequest.authenticatedPersonCode}`);
        this.$bvToast.toast(this.$t('SUCCESS_RESET_RATE_LIMIT'), {
          variant: 'success',
          solid: true,
        });
        // Refresh rate limit info after reset
        this.fetchRateLimitInfo();
      } catch (error) {
        console.error('Error resetting rate limit:', error);
        this.$bvToast.toast(this.$t('ERROR_RESET_RATE_LIMIT'), {
          variant: 'danger',
          solid: true,
        });
      }
    },
    statusVariant(result) {
      if (result === 'approved') return 'success';
      if (result === 'rejected') return 'danger';
      if (result === 'pending') return 'warning';
      return 'secondary';
    },
    formatResult(result) {
      if (!result) return '-';
      const resultMap = {
        approved: this.$t('APPROVED'),
        rejected: this.$t('REJECTED'),
        pending: this.$t('PENDING'),
        timeout: this.$t('MANUAL_REVIEW'),
      };
      return resultMap[result] || result;
    },
    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return (
        date.toLocaleDateString('et-EE') +
        ' ' +
        date.toLocaleTimeString('et-EE', { hour: '2-digit', minute: '2-digit' })
      );
    },
  },
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 2px solid #dee2e6;
}

dl {
  margin-bottom: 0;
}

dt {
  font-weight: 600;
  color: #495057;
  padding-bottom: 0.5rem;
}

dd {
  color: #212529;
  padding-bottom: 0.5rem;
}

/* Compact layout for Taotleja andmed section */
.compact-dl dt {
  padding-bottom: 0.25rem;
}

.compact-dl dd {
  padding-bottom: 0.25rem;
}

code {
  background-color: #f8f9fa;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.875rem;
  color: #e83e8c;
}

h5 {
  color: #343a40;
  font-weight: 600;
  font-size: 1.1rem;
}

.card-header h4 {
  margin-bottom: 0;
  display: flex;
  align-items: center;
}

.b-card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.btn-secondary {
  margin-right: 0.5rem;
}

.text-success {
  color: #28a745 !important;
}

.text-muted {
  color: #6c757d !important;
  font-style: italic;
}
</style>
